<template>
    <App title="Create Financier">
        <div class="w-100 position-relative">
            <div class="create-layout">

                <!-- HERO -->
                <div class="create-hero">
                    <div class="create-avatar">
                        <i class="fas fa-hand-holding-usd"></i>
                    </div>

                    <div class="create-hero-info">
                        <h2 class="create-name">New Financier</h2>
                        <span class="create-role-badge">Create</span>
                    </div>

                    <div class="create-hero-actions">
                        <a :href="route('financier.page.group')" class="btn btn-sm btn-outline-secondary">
                            <i class="fas fa-arrow-left me-1"></i> Back
                        </a>
                    </div>
                </div>

                <!-- CARD -->
                <div class="create-card">

                    <div class="create-card-header">
                        <i class="fas fa-user-tie me-2 text-muted"></i>
                        <span>Financier Details</span>
                    </div>

                    <Form ref="form" @submit="onSubmit">

                        <div class="create-form-grid">

                            <div class="create-field">
                                <label class="create-label">Name</label>
                                <InputField id="name" v-model="title" name="title" rules="required" label="Name" placeholder="Input name.." />
                            </div>

                            <div class="create-field">
                                <label class="create-label">Webhook</label>
                                <InputField id="webhook" type="webhook" v-model="webhook" name="webhook" rules="required" label="Webhook" placeholder="Input Webhook URL.." />
                            </div>

                            <div class="create-field">
                                <label class="create-label">Secret</label>
                                <InputField id="secret" type="secret" v-model="secret" name="secret" rules="required" label="Secret" placeholder="Input Secret.." />
                            </div>

                            <div class="create-field">
                                <label class="create-label">Email</label>
                                <InputField id="email" type="email" v-model="email" name="email" rules="required|email" label="Email" placeholder="Input email .." />
                            </div>

                            <div class="create-field">
                                <label class="create-label">Phone</label>
                                <PhoneField id="phone" v-model="phone" name="phone" rules="required|phone" label="Phone Number" placeholder="+234 800 000 0000" />
                            </div>

                            <div class="create-field">
                                <label class="create-label">Percentage</label>

                                <div class="input-group pct-input-group">
                                    <InputField id="percentage" v-model="percentage" type="number" name="percentage" rules="required" label="Percentage" placeholder="e.g. 10" @input="clampPct" />
                                    <span class="input-group-text pct-tag">%</span>
                                </div>
                            </div>

                        </div>

                        <!-- ACCOUNT -->
                        <div class="create-section-divider">
                            <i class="fas fa-university me-2 text-muted"></i>
                            <span>Account Information</span>
                        </div>

                        <div class="create-form-grid">

                            <div class="create-field">
                                <label class="create-label">Bank Name</label>

                                <SelectField id="bank" ref="bankField" v-model="account.bank" name="bank" rules="required" label="Bank Name" :options="banks_options" />
                            </div>

                            <div class="create-field">
                                <label class="create-label">Account Number</label>

                                <InputField id="number" v-model="account.number" name="number" rules="max:10|numeric" label="Account Number" placeholder="10-digit account number" :disabled="account.bank === ''" @blur="isAccountValid" />

                                <div v-if="validatingAccount" class="create-hint create-hint--muted">
                                    <i class="fas fa-spinner fa-spin me-1"></i> Validating account…
                                </div>

                                <div v-if="invalid_acctno_msg && !validatingAccount" class="create-hint create-hint--error">
                                    <i class="fas fa-exclamation-circle me-1"></i>{{ invalid_acctno_msg }}
                                </div>

                                <div v-if="account.name && !validatingAccount" class="create-hint create-hint--success">
                                    <i class="fas fa-check-circle me-1"></i>{{ account.name }}
                                </div>
                            </div>

                        </div>

                        <input type="hidden" v-model="account.index" />

                        <!-- ACTIONS -->
                        <div class="create-form-actions">
                            <button type="button" class="btn btn-outline-danger" @click="goBack">
                                <i class="fas fa-times me-1"></i> Cancel
                            </button>

                            <Button id="action" type="submit" :label="labels" :loading="loading" :done="done" class="btn btn-primary" :disabled="checkInput" />
                        </div>

                    </Form>
                </div>

            </div>
        </div>
    </App>
</template>

<script lang="ts">
import banks from '@/json/banks.json' // Your JSON file
import Button from '@/vue/components/Button.vue'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import PhoneField from '@/vue/components/PhoneField.vue'
import SelectField from '@/vue/components/SelectField.vue'
import App from '@/vue/templates/App.vue'

import { ButtonLabel } from '@/vue/utils/Button'
import Swal from 'sweetalert2'
import { Component, Prop, Ref, Vue, Watch, toNative } from 'vue-facing-decorator'

@Component({
    components: {
        App,
        Form,
        InputField,
        PhoneField,
        SelectField,
        Button,
    },
})
class Create extends Vue {

    message = ''
    loading = false
    done = false

    title = ''
    webhook = ''
    secret = ''
    email = ''
    phone = ''
    percentage = ''

    validatingAccount = false
    invalid_acctno_msg = ''

    account = {
        number: '',
        index: '',
        bank: '',
        name: '',

    }

    @Prop({ type: Object })
    readonly types!: Record<string, any>

    @Ref('form')
    readonly form!: InstanceType<typeof Form>

    @Ref('bankField')
    readonly bankField!: any

    get banks_options() {
        return {
            ajax: null,
            placeholder: 'Select Bank',
            minimumResultsForSearch: 2,
            data: banks.map(b => ({
                id: b.name,
                text: b.name,
            })),
        }
    }

    get labels(): ButtonLabel {
        return {
            normal: 'Create Financier',
            loading: 'Creating...',
            done: 'Created',
        }
    }

    get checkInput(): boolean {
        return [this.title, this.percentage, this.email, this.phone]
            .some(field => String(field ?? '').trim() === '')
    }


    showToast(message: string, type: 'success' | 'error') {

        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: type,
            title: message,
            showConfirmButton: false,
            timer: 5000,
        })
    }

    clampPct(e: Event) {
        const input = e.target as HTMLInputElement
        const val = input.valueAsNumber

        if (!isNaN(val)) {
            if (val > 100) this.percentage = '100'
            if (val < 0) this.percentage = '0'
        }
    }

    @Watch('account.bank')
    onFetchAccountName(value: string) {
        if (typeof value === 'undefined') {
            this.account.index = ''
            return
        }

        if (value.length < 10) {
            this.account.index = ''
            return
        }

        this.message = ''

        if (value) {
            const selectedBank = banks.find(b => b.name === value)
            if (selectedBank) {
                this.account.index = selectedBank.index

            }
        }
        else {
            this.account.index = ''

        }
    }



    async isAccountValid(): Promise<boolean> {
        if (!this.account.number || !this.account.index) return false

        // Already validated — skip the network call
        if (this.account.name) return true

        if (import.meta.env.VITE_APP_ENV === 'local') {
            this.account.name = 'Test Account Name'
            return true
        }

        this.validatingAccount = true
        this.invalid_acctno_msg = ''

        this.validatingAccount = true


        try {
            const response = await this.$http.post(this.route('account.ajax.check'), {
                number: this.account.number,
                index: this.account.index,
            })

            if (response?.data?.data?.name) {
                this.account.name = response.data.data.name
                this.invalid_acctno_msg = ''
                return true
            } else {
                this.account.name = ''
                this.invalid_acctno_msg = 'Could not fetch account name'
                return false
            }
        } catch (error) {
            this.account.name = ''
            this.invalid_acctno_msg = 'Error validating account'
            this.showToast('Error validating account', 'error')
        } finally {
            this.validatingAccount = false

        }

    }

    async onSubmit(payload) {
        this.loading = true
        this.message = ''
        payload.index = this.account.index

        const isValid = await this.isAccountValid()
        if (!isValid) {
            this.invalid_acctno_msg = 'Invalid account number. Please try again!'
            this.loading = false

            return
        }

        this.$http
            .post(this.route('financier.ajax.create'), payload)
            .then((response) => {
                this.done = true
                this.message = response.data.message || 'Financier created successfully!'
                Swal.fire({
                    title: 'Success!',
                    text: this.message,
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    this.router.visit(this.route('financier.page.group'))
                    this.message = ''
                    Swal.close()

                })
                    .catch((e) => {
                        if (e.response && e.response.data && e.response.data.errors) {
                            const errorMsg = e.response.data.errors.title
                                ? e.response.data.errors.title[0]
                                : (e.response.data.message || 'An error occurred.')

                            this.showToast(errorMsg, 'error')
                        }

                        else if (e.message) {
                            this.showToast(e.message, 'error')
                        }
                        else {
                            this.showToast('Something went wrong. Please try again.', 'error')
                        }

                    })
                    .finally(() => (this.loading = false))
            })
    }

    goBack() {
        window.history.back()
    }
}

export default toNative(Create)
</script>
<style scoped lang="scss">
.create-layout {
    max-width: 760px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.create-hero {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    background: #fff;
    border-radius: 12px;
    padding: 1.5rem 1.75rem;
    border: 1px solid #e9ecef;
}

.create-avatar {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: linear-gradient(135deg, #1e3a2f, #3a7d52);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
}

.create-name {
    font-size: 1.15rem;
    font-weight: 700;
}

.create-role-badge {
    background: #dcfce7;
    color: #15803d;
    padding: 0.2rem 0.7rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
}

.create-card {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 12px;
}

.create-card-header {
    padding: 0.875rem 1.75rem;
    border-bottom: 1px solid #f1f3f5;
    background: #f9fafb;
}

.create-form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
    padding: 1.5rem;
}

@media (max-width: 560px) {
    .create-form-grid {
        grid-template-columns: 1fr;
    }
}

.create-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: #6b7280;
}

.create-form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
    padding: 1rem 1.75rem;
    background: #f9fafb;
}

.create-hint {
    font-size: 0.8rem;
    margin-top: 0.3rem;
}

.create-hint--error {
    color: #dc2626;
}

.create-hint--success {
    color: #16a34a;
}

.create-hint--muted {
    color: #6b7280;
}
</style>
